/**
 * Dois conjuntos de limites que não devem ser confundidos.
 *
 * `HARD_LIMITS` são restrições reais: a VTEX ou o Merchant Center rejeitam
 * o valor. Violá-los bloqueia a sugestão.
 *
 * `SOFT_LIMITS` são convenções de mercado (SERP/UX). O Google não publica
 * limite de caracteres para title ou meta — são heurísticas de pixel.
 *
 * Na geração, alguns softs são teto operacional (retry + truncate word-aware):
 * `title`, `imageAltText`, `metaTagDescriptionIdealMax`. A UI também usa softs
 * como contador de aviso sem bloquear edição manual.
 */

import type { EnrichableField } from './enums';
import { normalize } from '../util/text';

export const HARD_LIMITS = {
  /** Campo `Name` do produto na VTEX. */
  name: 150,
  /** Campo `Name` (SkuName) do SKU na Catalog API. */
  skuName: 150,
  /** Campo `Title` do produto na VTEX. */
  title: 150,
  /** Campo `MetaTagDescription` do produto na VTEX. */
  metaTagDescription: 150,
  /** `description` do feed do Merchant Center. */
  feedDescriptionMax: 5000,
  feedDescriptionMin: 1,
  /** `title` do feed do Merchant Center. */
  feedTitleMax: 150,
  /** `FieldName` de especificação na VTEX. */
  specificationFieldName: 100,
  /** Alt text (`Text`) da imagem de SKU — teto conservador. */
  imageAltText: 250,
  /** `Label` interno da imagem de SKU na VTEX. */
  imageLabel: 100,
} as const;

export const SOFT_LIMITS = {
  /**
   * Convenção SERP para title — teto operacional de geração (retry + clamp).
   * Não é limite oficial do Google.
   */
  title: 60,
  /**
   * Heurística SERP (~pixel) genérica para meta. NÃO usar como alvo de geração
   * na VTEX: o hard `HARD_LIMITS.metaTagDescription` (150) é mais apertado.
   * Preferir `metaTagDescriptionIdealMin` / `IdealMax` na geração.
   */
  metaTagDescription: 160,
  /** Faixa ideal de meta description na geração (VTEX hard = 150). */
  metaTagDescriptionIdealMin: 120,
  metaTagDescriptionIdealMax: 145,
  /** Mínimo prático para uma descrição ser útil ao usuário e ao Google. */
  descriptionMin: 200,
  descriptionIdealMin: 400,
  descriptionIdealMax: 2000,
  /**
   * Alt text — teto operacional de geração (retry + clamp) para a11y/SEO de imagem.
   */
  imageAltText: 125,
  /** Máximo de termos na lista de keywords (busca interna). */
  keywordsMaxTerms: 10,
  /**
   * Nome de produto útil no catálogo/busca interna: abaixo disso (ex.: "iphone")
   * falta atributo distintivo.
   */
  nameMinChars: 8,
  /** Acima de 2 tokens o nome costuma carregar tipo + atributo. */
  nameMinTokens: 3,
} as const;

export const SOFT_LIMIT_NOTE =
  'Convenção de mercado para evitar truncamento na SERP. O Google não publica limite de caracteres — o corte é por pixel.';

export type HardLimitKey = keyof typeof HARD_LIMITS;
export type SoftLimitKey = keyof typeof SOFT_LIMITS;

/** Limite rígido aplicável ao campo enriquecível (UI / validação). */
export function hardLimitForField(field: EnrichableField): number | undefined {
  switch (field) {
    case 'description':
      return HARD_LIMITS.feedDescriptionMax;
    case 'name':
      return HARD_LIMITS.name;
    case 'skuName':
      return HARD_LIMITS.skuName;
    case 'title':
      return HARD_LIMITS.title;
    case 'metaTagDescription':
      return HARD_LIMITS.metaTagDescription;
    case 'imageAltText':
      return HARD_LIMITS.imageAltText;
    case 'imageLabel':
      return HARD_LIMITS.imageLabel;
    default:
      return undefined;
  }
}

/**
 * Teto soft exibido no contador (aviso SERP/UX).
 * Meta usa `IdealMax` (145), não a heurística genérica de 160.
 */
export function softDisplayLimitForField(field: EnrichableField): number | undefined {
  switch (field) {
    case 'title':
      return SOFT_LIMITS.title;
    case 'metaTagDescription':
      return SOFT_LIMITS.metaTagDescriptionIdealMax;
    case 'imageAltText':
      return SOFT_LIMITS.imageAltText;
    case 'description':
      return SOFT_LIMITS.descriptionIdealMax;
    default:
      return undefined;
  }
}

export interface CatalogNameAssessment {
  ok: boolean;
  tooFewChars: boolean;
  tooFewTokens: boolean;
  length: number;
  tokenCount: number;
}

/** Avalia se um Name/SkuName atinge o mínimo prático de catálogo. */
export function assessCatalogName(value: string): CatalogNameAssessment {
  const length = value.trim().length;
  const tokenCount = normalize(value.trim())
    .split(/\s+/)
    .filter((token) => token.length > 0).length;
  const tooFewChars = length < SOFT_LIMITS.nameMinChars;
  const tooFewTokens = tokenCount > 0 && tokenCount < SOFT_LIMITS.nameMinTokens;
  return {
    ok: !tooFewChars && !tooFewTokens,
    tooFewChars,
    tooFewTokens,
    length,
    tokenCount,
  };
}
